// src/components/modals/CrearProductoModal.jsx
import React, { useState, useEffect } from 'react';
import { post, get, uploadProductImage } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import useAuthStore from '../../store/authStore';
import { FaImage, FaSpinner } from 'react-icons/fa';

function CrearProductoModal({ isOpen, onClose, onProductoCreated }) {
    const [nombre, setNombre] = useState('');
    const [precio, setPrecio] = useState(0);
    const [descripcion, setDescripcion] = useState('');
    const [cantidad, setCantidad] = useState(0);
    const [stockMinimo, setStockMinimo] = useState(0);
    const [categorias, setCategorias] = useState([]);
    const [selectedCategoria, setSelectedCategoria] = useState('');
    const [subcategoria, setSubcategoria] = useState('');
    const [etiquetas, setEtiquetas] = useState([]);
    const [nuevaEtiqueta, setNuevaEtiqueta] = useState('');
    const [activo, setActivo] = useState(true);
    const [marcaId, setMarcaId] = useState(null);
    const { user } = useAuthStore();
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchMarcaId();
        }
    }, [isOpen]);

    useEffect(() => {
        if (marcaId) {
            fetchCategorias();
        }
    }, [marcaId]);

    const fetchMarcaId = async () => {
        try {
            const marcas = await get(`marcas/user/${user.userId}`);
            if (marcas.length > 0) {
                setMarcaId(marcas[0]._id);
            }
        } catch (error) {
            console.error('Error fetching marca:', error);
        }
    };

    const fetchCategorias = async () => {
        try {
            if (!marcaId) return;
            const response = await get(`productos/${marcaId}/categorias?activa=true`);
            setCategorias(response);
        } catch (error) {
            console.error('Error fetching categorias:', error);
            Swal.fire({
                title: 'Error',
                text: error.message,
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        console.log('Selected file:', file);
        if (file) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!validTypes.includes(file.type.toLowerCase())) {
                console.log('Invalid file type:', file.type);
                Swal.fire({
                    title: 'Error',
                    text: 'El archivo debe ser una imagen (jpg, jpeg, png o gif)',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }

            // Validate file size (5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                console.log('File too large:', file.size);
                Swal.fire({
                    title: 'Error',
                    text: 'La imagen no debe superar los 5MB',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }

            // Validate file extension
            const fileName = file.name.toLowerCase();
            const validExtensions = ['.jpg', '.jpeg', '.png', '.gif'];
            if (!validExtensions.some(ext => fileName.endsWith(ext))) {
                console.log('Invalid file extension:', fileName);
                Swal.fire({
                    title: 'Error',
                    text: 'El archivo debe tener una extensión válida (jpg, jpeg, png o gif)',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
                return;
            }

            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                console.log('Image preview generated');
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCreate = async () => {
        if (categorias.length === 0) {
            Swal.fire({
                title: 'Error',
                text: 'Debe crear al menos una categoría antes de crear productos.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
            return;
        }

        if (!nombre.trim()) {
            Swal.fire({
                title: 'Error',
                text: 'El nombre del producto es obligatorio.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
            return;
        }

        if (!selectedCategoria) {
            Swal.fire({
                title: 'Error',
                text: 'Debe seleccionar una categoría.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
            return;
        }

        if (!marcaId) {
            Swal.fire({
                title: 'Error',
                text: 'No se pudo identificar la marca. Inicie sesión nuevamente.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
            return;
        }

        try {
            // 1. Crear el producto
            const productoData = {
                nombre: nombre.trim(),
                precio: Number(precio),
                descripcion: descripcion.trim(),
                cantidad: Number(cantidad),
                stockMinimo: Number(stockMinimo),
                categoria: selectedCategoria,
                subcategoria: subcategoria.trim(),
                etiquetas: etiquetas,
                activo,
                marca: marcaId
            };

            console.log('Creating product with data:', productoData);
            const productoCreado = await post(`productos/${marcaId}`, productoData);
            console.log('Product created:', productoCreado);

            // 2. Si hay imagen seleccionada, subirla
            if (selectedImage && productoCreado._id) {
                setIsUploadingImage(true);
                try {
                    console.log('Uploading image for product:', productoCreado._id);
                    await uploadProductImage(productoCreado._id, selectedImage);
                    console.log('Image uploaded successfully');
                } catch (error) {
                    console.error('Error uploading image:', error);
                    // Mostrar advertencia pero no interrumpir el proceso
                    Swal.fire({
                        title: 'Advertencia',
                        text: 'El producto se creó correctamente pero hubo un error al subir la imagen. Puede intentar subir la imagen más tarde editando el producto.',
                        icon: 'warning',
                        confirmButtonText: 'Aceptar'
                    });
                } finally {
                    setIsUploadingImage(false);
                }
            }

            // 3. Mostrar mensaje de éxito y cerrar modal
            await Swal.fire({
                title: 'Producto Creado',
                text: 'El producto ha sido creado exitosamente',
                icon: 'success',
                confirmButtonText: 'Aceptar'
            });

            // 4. Limpiar y cerrar
            limpiarCampos();
            onProductoCreated(); // Esto debería actualizar la lista de productos
            onClose();

        } catch (error) {
            console.error('Error creating product:', error);
            // Solo mostrar error si no es un error de servidor interno
            if (error.message && !error.message.includes('Internal server error')) {
                Swal.fire({
                    title: 'Error',
                    text: error.message,
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            } else {
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo crear el producto. Intente de nuevo.',
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        }
    };

    const agregarEtiqueta = () => {
        if (nuevaEtiqueta.trim() && !etiquetas.includes(nuevaEtiqueta.trim())) {
            setEtiquetas([...etiquetas, nuevaEtiqueta.trim()]);
            setNuevaEtiqueta('');
        }
    };

    const eliminarEtiqueta = (etiquetaAEliminar) => {
        setEtiquetas(etiquetas.filter(etiqueta => etiqueta !== etiquetaAEliminar));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            agregarEtiqueta();
        }
    };

    const limpiarCampos = () => {
        setNombre('');
        setPrecio(0);
        setDescripcion('');
        setCantidad(0);
        setStockMinimo(0);
        setSelectedCategoria('');
        setSubcategoria('');
        setEtiquetas([]);
        setNuevaEtiqueta('');
        setActivo(true);
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleClose = () => {
        limpiarCampos();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 sm:p-6 rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl sm:text-2xl font-bold mb-4">Crear Producto</h2>

                {/* Image upload section */}
                <div className="mb-6 border-b pb-6">
                    <label className="block mb-2 font-semibold">Imagen del Producto</label>
                    <div className="flex items-start space-x-4">
                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden">
                            {imagePreview ? (
                                <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                                <FaImage className="w-8 h-8 text-gray-400" />
                            )}
                        </div>
                        <div className="flex-1">
                            <input
                                type="file"
                                accept="image/jpeg,image/jpg,image/png,image/gif"
                                onChange={handleImageChange}
                                className="hidden"
                                id="producto-imagen"
                            />
                            <label
                                htmlFor="producto-imagen"
                                className="inline-block px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 cursor-pointer transition-colors mb-2"
                            >
                                Seleccionar Imagen
                            </label>
                            <p className="text-sm text-gray-500">
                                Formatos permitidos: JPG, JPEG, PNG, GIF
                                <br />
                                Tamaño máximo: 5MB
                            </p>
                            {selectedImage && (
                                <p className="text-sm text-gray-600 mt-2">
                                    Archivo seleccionado: {selectedImage.name}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-2 font-semibold">Nombre *</label>
                        <input
                            type="text"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="Nombre del producto"
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 font-semibold">Precio *</label>
                        <input
                            type="text"
                            value={precio}
                            onChange={(e) => {
                                const input = e.target.value.replace(',', '.');
                                if (/^\d*\.?\d*$/.test(input)) {
                                    setPrecio(input);
                                }
                            }}
                            onBlur={() => {
                                if (precio) {
                                    setPrecio(parseFloat(precio) || 0);
                                }
                            }}
                            placeholder="0.00"
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 font-semibold">Cantidad</label>
                        <input
                            type="number"
                            value={cantidad}
                            onChange={(e) => setCantidad(Number(e.target.value) || 0)}
                            min="0"
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 font-semibold">Stock Mínimo</label>
                        <input
                            type="number"
                            value={stockMinimo}
                            onChange={(e) => setStockMinimo(Number(e.target.value) || 0)}
                            min="0"
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 font-semibold">Categoría *</label>
                        {categorias.length === 0 ? (
                            <div className="w-full p-2 border border-red-300 rounded bg-red-50 text-red-600 text-sm">
                                No hay categorías disponibles. Debe crear una categoría primero.
                            </div>
                        ) : (
                            <select
                                value={selectedCategoria}
                                onChange={(e) => setSelectedCategoria(e.target.value)}
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Seleccionar categoría</option>
                                {categorias.map((categoria) => (
                                    <option key={categoria._id} value={categoria._id}>
                                        {categoria.nombre}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div>
                        <label className="block mb-2 font-semibold">Subcategoría</label>
                        <input
                            type="text"
                            value={subcategoria}
                            onChange={(e) => setSubcategoria(e.target.value)}
                            placeholder="Ej: Ropa Deportiva"
                            className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block mb-2 font-semibold">Descripción</label>
                    <textarea
                        value={descripcion}
                        onChange={(e) => setDescripcion(e.target.value)}
                        placeholder="Descripción del producto"
                        rows="3"
                        className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    ></textarea>
                </div>

                <div className="mt-4">
                    <label className="block mb-2 font-semibold">Etiquetas</label>
                    <div className="flex gap-2 mb-2">
                        <input
                            type="text"
                            value={nuevaEtiqueta}
                            onChange={(e) => setNuevaEtiqueta(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Agregar etiqueta"
                            className="flex-1 p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                            onClick={agregarEtiqueta}
                            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                        >
                            +
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {etiquetas.map((etiqueta, index) => (
                            <span
                                key={index}
                                className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm flex items-center gap-1"
                            >
                                {etiqueta}
                                <button
                                    onClick={() => eliminarEtiqueta(etiqueta)}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                </div>

                <div className="mt-4">
                    <label className="flex items-center">
                        <input
                            type="checkbox"
                            checked={activo}
                            onChange={(e) => setActivo(e.target.checked)}
                            className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Producto activo</span>
                    </label>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                    <button
                        onClick={handleClose}
                        className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={categorias.length === 0 || isUploadingImage}
                        className={`px-4 py-2 rounded transition-colors flex items-center ${categorias.length === 0 || isUploadingImage
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                    >
                        {isUploadingImage && <FaSpinner className="animate-spin mr-2" />}
                        Crear
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CrearProductoModal;